import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { UserPageLocationState } from '@user/pages/UserPageLocationState';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { FlatButton, Loading } from '@framework/ui/atoms';
import type { GroupMemberInvitation } from '@group/domain';
import type { UserId } from 'balus-common';
import { useActionLogSender } from '@framework/action-log';
import { UserKey } from '@user/domain';
import { createOnboardingViewModel } from './createOnboardingViewModel';
import { trpcClient } from '@framework/trpcClient';
import { TRPCClientError } from '@trpc/client';
import { createErrorMessage } from '@framework/utils/createErrorMessage';

type Props = {
  currentUserId: UserId;
  invitation: GroupMemberInvitation;
};

export const AuthenticatedMenu: React.FC<Props> = ({ currentUserId, invitation }: Props) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation() as { state?: UserPageLocationState };
  const actionLogSender = useActionLogSender();
  const handleInvitationAccept = useCallback(async () => {
    setLoading(true);
    const { groupId, id: invitationId, acceptedUserId } = invitation;
    const isAccepted = acceptedUserId !== null;

    try {
      // グループへの招待を承諾する
      await trpcClient.group.joinGroupAndWorkspacesWithInvitation.mutate({
        groupId,
        invitationId,
        isAccepted,
      });

      // ワークスペースへの参加処理（memberRoleに基づく分岐はバックエンドで実施）
      const { personalWorkspaceId } = await trpcClient.group.joinWorkspacesOnInvitationAccept.mutate({
        groupId,
      });

      // ゲストの場合は招待されたグループ画面に遷移して終了
      if (!personalWorkspaceId) {
        navigate(UserPagePathBuilder.groupPage(invitation.groupId));
        return;
      }

      // オンボーディング用ビューモデルを作成する
      const onboardingViewModelId = await createOnboardingViewModel(
        invitation.groupId,
        UserKey.buildFromId(currentUserId),
        personalWorkspaceId,
        actionLogSender
      );

      if (onboardingViewModelId) {
        navigate(UserPagePathBuilder.viewModelPage(onboardingViewModelId));
        return;
      }
      navigate(UserPagePathBuilder.groupPage(invitation.groupId));
    } catch (e) {
      if (e instanceof TRPCClientError) {
        setErrorMessage(
          createErrorMessage(
            e,
            'グループへの参加に失敗しました。少し時間を空けてからもう一度試してみるか、サポート担当者にお問い合わせください。'
          )
        );
      } else {
        throw e;
      }
    } finally {
      setLoading(false);
    }
  }, [currentUserId, navigate, invitation, actionLogSender]);

  // 外部認証プロバイダを利用した新規ユーザ登録の完了後にこの画面に戻ってきた場合には、
  // 自動で招待の受諾処理を行う。
  useEffect(() => {
    if (location.state?.signUpFinished) {
      handleInvitationAccept();
    }
  }, [handleInvitationAccept, location]);

  return (
    <>
      <div className="py-4">
        {loading ? (
          <Loading />
        ) : (
          !errorMessage && (
            <FlatButton color="brand" className="px-8" onClick={handleInvitationAccept}>
              グループに参加する
            </FlatButton>
          )
        )}
      </div>

      {errorMessage && <span className="text-red-700">{errorMessage}</span>}
    </>
  );
};
